<template>
  <div class="gestione-sensore-rh-container">
    <sidebar 
      :noSocial="true" 
      :backOn="true" 
    />
    <div class="device-container">
        <deviceCard 
            :small="true"
            :content="newData" 
        />
        <div class="xs-icon-card">
            <img src="@/assets/gestisci_sensori.png">
            <p>{{ $t('rhSensorManagement') }}</p>
        </div>
    </div>
    <div class="content">
      <IdroTitle :title="$t('rhSensorManagement')"/>
      <h2 class="font-bold text-xl">{{title}}</h2>
        <div class="main">
            <form @submit.prevent="onSubmit" class="form-container">
            <div class="frame">
                <div class="frame-header">
                <span class="font-bold text-xl">{{ $t('humidity') }}</span>
                </div>
                <div class="frame-content">
                <!-- Umidità Section with Range Slider -->
                <div class="umidita-section">
                    <div class="umidita-input-group">
                    <label class="font-semibold">{{ $t('low') }}</label>
                    <input 
                        type="number" 
                        v-model.number="satData.umiditaBassa"
                        :disabled="isLoading"
                        :min="umiditaMin"
                        :max="umiditaMax"
                        @input="updateSliderFromInput"
                        class="umidita-input"
                    >
                    </div>
                    
                    <!-- Range Slider -->
                    <div class="slider-wrapper">
                    <VueSlider 
                        v-model="satData.umidita"
                        :min="umiditaMin" 
                        :max="umiditaMax"
                        :enableCross="false"
                        :disabled="isLoading"
                        @change="syncValues"
                        @dragging="syncValues"
                    />
                    </div>

                    <div class="umidita-input-group">
                    <label class="font-semibold">{{ $t('high') }}</label>
                    <input 
                        type="number" 
                        v-model.number="satData.umiditaAlta"
                        :disabled="isLoading"
                        :min="umiditaMin"
                        :max="umiditaMax"
                        @input="updateSliderFromInput"
                        class="umidita-input"
                    >
                    </div>
                </div>

                <!-- Ferma Programma Section -->
                <div class="programma-section">
                    <h3 class="font-semibold text-lg mb-4">{{ $t('programStop') }}</h3>
                    <div class="programma-grid">
                    <!-- Left Column -->
                    <div class="programma-column">
                        <div class="programma-item" v-for="n in [1, 3, 5, 7]" :key="'prog-' + n">
                        <label :for="'programma-' + n" class="programma-label">
                            {{ $t('program') }} {{ n }}
                        </label>
                        <input 
                            :id="'programma-' + n" 
                            type="checkbox" 
                            v-model="programmi['prog' + n]"
                            :disabled="isLoading"
                            class="programma-checkbox"
                        >
                        </div>
                    </div>
                    
                    <!-- Right Column -->
                    <div class="programma-column">
                        <div class="programma-item" v-for="n in [2, 4, 6, 8]" :key="'prog-' + n">
                        <label :for="'programma-' + n" class="programma-label">
                            {{ $t('program') }} {{ n }}
                        </label>
                        <input 
                            :id="'programma-' + n" 
                            type="checkbox" 
                            v-model="programmi['prog' + n]"
                            :disabled="isLoading"
                            class="programma-checkbox"
                        >
                        </div>
                    </div>
                    </div>
                </div>

                <!-- Save Button -->
                <div class="save-button-wrapper">
                    <MyButton
                    type="submit"
                    class="filled__yellow"
                    :label="$t('save')"
                    :loading="postControlIsLoading"
                    />
                </div>
                </div>
            </div>
            </form>
        </div>
    </div>
  </div>
</template>

<script setup>
import { useDevicesStore } from '@/stores/DevicesStore'
import { useDataStore } from '@/stores/DataStore'
import { storeToRefs } from 'pinia'
import { defineAsyncComponent, computed, onMounted, ref } from 'vue'
import VueSlider from 'vue-slider-component'
import 'vue-slider-component/theme/default.css'
import MyButton from '@/components/button/BaseButton.vue'

// Props
const props = defineProps({
  id: String
})

// Async component
const deviceCard = defineAsyncComponent(
  () => import('@/components/cards/deviceCard.vue')
)

// Stores
const deviceStore = useDevicesStore()
const dataStore = useDataStore()
const { isLoading } = storeToRefs(useDevicesStore())
const { postControlIsLoading } = storeToRefs(useDataStore())

const newData = computed(() => {
  return [deviceStore.deviceData]
})

// Data
const title = ref('-')
const umiditaMin = ref(0)
const umiditaMax = ref(100)

const satConfigParams = ref({
  fields: 'S1061,S1062,S10008,S11008,S12008,S13008,S14008,S15008,S16008,S17008',
  measurement: 'SATCONFIG',
  device_code: null
})

const satData = ref({
  umidita: [30, 70],
  umiditaAlta: 70,
  umiditaBassa: 30
})

const programmi = ref({
  prog1: false,
  prog2: false,
  prog3: false,
  prog4: false,
  prog5: false,
  prog6: false,
  prog7: false,
  prog8: false
})

const postData = ref({
  command: 'SATCONFIG',
  payload: {}
})

function updateSliderFromInput() {
  // Ensure bassa is always less than or equal to alta
  const min = Math.min(satData.value.umiditaBassa, satData.value.umiditaAlta)
  const max = Math.max(satData.value.umiditaBassa, satData.value.umiditaAlta)
  satData.value.umiditaBassa = min
  satData.value.umiditaAlta = max
  satData.value.umidita = [min, max]
}

// Methods
function syncValues() {
  // Ensure values from slider are properly assigned
  const min = Math.min(satData.value.umidita[0], satData.value.umidita[1])
  const max = Math.max(satData.value.umidita[0], satData.value.umidita[1])
  satData.value.umiditaBassa = min
  satData.value.umiditaAlta = max
  satData.value.umidita = [min, max]
}

async function fillSatData() {
  if (dataStore.satConfig) {
    // Load humidity values
    if (dataStore.satConfig.S1061 !== undefined) {
      satData.value.umiditaBassa = parseInt(dataStore.satConfig.S1061)
    }
    if (dataStore.satConfig.S1062 !== undefined) {
      satData.value.umiditaAlta = parseInt(dataStore.satConfig.S1062)
    }
    satData.value.umidita = [satData.value.umiditaBassa, satData.value.umiditaAlta]
    
    // Load program checkboxes
    for (let i = 1; i <= 8; i++) {
      const fieldName = 'S' + (10008 + (i - 1) * 1000)
      if (dataStore.satConfig[fieldName] !== undefined) {
        programmi.value['prog' + i] = dataStore.satConfig[fieldName] == 1
      }
    }
  }
}

async function onSubmit() {
  postData.value.payload = {}
  
  // Save humidity values
  postData.value.payload.S1061 = String(satData.value.umiditaBassa)
  postData.value.payload.S1062 = String(satData.value.umiditaAlta)
  
  // Save program checkboxes
  for (let i = 1; i <= 8; i++) {
    const fieldName = 'S' + (10008 + (i - 1) * 1000)
    postData.value.payload[fieldName] = programmi.value['prog' + i] ? '1' : '0'
  }
  
  await dataStore.postControl(satConfigParams.value.device_code, postData.value)
}

// Lifecycle
onMounted(async () => {
    await deviceStore.loadDevice(props.id)
    satConfigParams.value.device_code = deviceStore.deviceData.code
    title.value = 'Idrosat:' + deviceStore.deviceData.name
    await dataStore.getLastSatConfig(satConfigParams.value)
    await fillSatData()
    syncValues()
})
</script>

<style scoped>
.gestione-sensore-rh-container {
  @apply 
    relative flex flex-col 
    px-[16px] md:px-[200px] lg:px-[260px] xl:px-[320px] 2xl:px-[360px]
}

.device-container {
  @apply 
    flex sm:flex-col fixed items-end sm:items-center gap-2
    bottom-0 left-4
    pb-4 sm:pb-8
}

.xs-icon-card {
  @apply flex flex-col items-center justify-center p-4 bg-slate-50 rounded-lg;
}

.xs-icon-card img {
  @apply w-20 h-20 mb-2;
}

.xs-icon-card p {
  @apply text-center font-semibold text-gray-700;
}

.content {
  @apply 
    flex flex-col justify-center
    gap-2
    w-full
    sm:gap-4
    sm:my-[20px]
}

.main {
  @apply flex flex-col w-full justify-center items-center relative;
}

.form-container {
  @apply 
    w-full
    relative
    h-[300px] sm:h-full
    overflow-auto sm:overflow-visible
}

.frame {
  @apply  
    flex flex-col
    bg-[#DDE8FA]/60 backdrop-blur-lg
    w-full mb-10
}

.frame-header {
  @apply
    flex justify-center
    border-b-2 border-[#3a3a3e]
    py-3 px-5
}

.frame-content {
  @apply p-6 space-y-8;
}

/* Umidità Section */
.umidita-section {
  @apply 
    flex flex-col sm:flex-row 
    items-center gap-4
    py-4 px-5
    border-b-4 border-yellow-100
}

.umidita-input-group {
  @apply flex flex-col items-center gap-2 w-full sm:w-32;
}

.umidita-input {
  @apply 
    px-3 py-2 
    border border-gray-300 rounded-md 
    focus:outline-none focus:ring-2 focus:ring-blue-500
    w-full text-center
}

.slider-wrapper {
  @apply flex-1 w-full px-4 py-2;
}

/* Ferma Programma Section */
.programma-section {
  @apply py-4 px-5;
}

.programma-grid {
  @apply 
    grid grid-cols-1 sm:grid-cols-2 
    gap-x-8 gap-y-4
}

.programma-column {
  @apply space-y-4;
}

.programma-item {
  @apply flex items-center justify-between gap-4;
}

.programma-label {
  @apply font-normal cursor-pointer select-none flex-1;
}

.programma-checkbox {
  @apply 
    w-5 h-5 
    cursor-pointer
    accent-blue-600
}

/* Save Button */
.save-button-wrapper {
  @apply flex justify-center mt-6 px-5 pb-4;
}

.save-button-wrapper button {
  @apply w-full max-w-4xl;
}

input[type="number"] {
  @apply pl-2 rounded py-2 w-full
}
</style>
